import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();

export const applyToSimilarRequestSchema = z.object({
  operation_id: uuidSchema,
  preview_id: uuidSchema,
  accept_ids: z.array(uuidSchema).min(1).max(200),
  create_future_condition: z.boolean(),
}).strict().superRefine((request, context) => {
  if (new Set(request.accept_ids).size !== request.accept_ids.length) {
    context.addIssue({ code: 'custom', path: ['accept_ids'], message: 'accept_ids must be unique' });
  }
});
export type ApplyToSimilarRequest = z.infer<typeof applyToSimilarRequestSchema>;

export const applyToSimilarCommitDataSchema = z.object({
  rule_id: uuidSchema.nullable(),
  batch_id: uuidSchema,
  applied_count: z.number().int().nonnegative(),
  unchanged_count: z.number().int().nonnegative(),
  skipped_count: z.number().int().nonnegative(),
  accepted_ids: z.array(uuidSchema),
  new_matches_since_preview: z.number().int().nonnegative(),
}).strict().superRefine((result, context) => {
  if (new Set(result.accepted_ids).size !== result.accepted_ids.length) {
    context.addIssue({ code: 'custom', path: ['accepted_ids'], message: 'accepted_ids must be unique' });
  }
  if (result.applied_count + result.unchanged_count + result.skipped_count !== result.accepted_ids.length) {
    context.addIssue({ code: 'custom', path: ['applied_count'], message: 'commit counts must cover accepted_ids' });
  }
});
export type ApplyToSimilarCommitData = z.infer<typeof applyToSimilarCommitDataSchema>;

export const applyToSimilarResponseMetaSchema = z.object({
  idempotent_replay: z.boolean(),
}).strict();
export type ApplyToSimilarResponseMeta = z.infer<typeof applyToSimilarResponseMetaSchema>;

export interface ApplyToSimilarInput {
  lineItemId: string;
  previewId?: string;
  acceptIds?: string[];
  createFutureCondition?: boolean;
  /** @deprecated Legacy callers must be moved to preview-first commit. */
  scope?: 'all_time';
  /** @deprecated Legacy callers must be moved to preview-first commit. */
  categoryId?: string;
  /** @deprecated Legacy callers must be moved to preview-first commit. */
  isBusiness?: boolean;
  /** Reuse this identity when deliberately replaying an unknown result. */
  operationId?: string;
}

export interface ApplyToSimilarResult {
  data: ApplyToSimilarCommitData;
  meta: ApplyToSimilarResponseMeta;
  /** Compatibility projection for the existing category correction controller. */
  applied_count: number;
}

function newOperationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new Error('operation ID generation is unavailable');
  return uuidSchema.parse(generated);
}

function requestFor(input: ApplyToSimilarInput): { lineItemId: string; body: ApplyToSimilarRequest } {
  const lineItemId = uuidSchema.parse(input.lineItemId);
  if (!input.previewId || !input.acceptIds || input.createFutureCondition === undefined) {
    throw new Error('A similar preview is required before applying to similar items.');
  }
  return {
    lineItemId,
    body: applyToSimilarRequestSchema.parse({
      operation_id: input.operationId ?? newOperationId(),
      preview_id: input.previewId,
      accept_ids: input.acceptIds,
      create_future_condition: input.createFutureCondition,
    }),
  };
}

export function useApplyToSimilar() {
  const queryClient = useQueryClient();
  const operationIdRef = useRef<string | undefined>(undefined);
  const mutation = useMutation<ApplyToSimilarResult, Error, ApplyToSimilarInput>({
    mutationFn: async (input) => {
      const request = requestFor(input);
      operationIdRef.current = request.body.operation_id;
      const response = await client.post(`/api/line-items/${request.lineItemId}/apply-to-similar`, request.body);
      const parsed = parseEnvelope(applyToSimilarCommitDataSchema, response.data);
      return {
      data: parsed.data,
      meta: applyToSimilarResponseMetaSchema.parse(parsed.meta ?? {}),
      applied_count: parsed.data.applied_count,
      };
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-items'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      queryClient.invalidateQueries({ queryKey: ['transactions', 'audit'] });
    },
  });

  return {
    ...mutation,
    get operationId() {
      return operationIdRef.current;
    },
  };
}
